import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent, ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

/**
 * @typedef {object} StarterPackSelectorRefs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} addToCartText
 * @property {HTMLElement} addToCartSpinner
 * @property {HTMLElement} addToCartTextError
 * @property {HTMLElement} panelLoading
 */

class StarterPackSelector extends Component {
  /** @type {StarterPackSelectorRefs} */
  refs = {};

  requiredRefs = ["addToCartButton", "addToCartText", "addToCartSpinner"];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, HTMLElement>} */
  #panelCache = new Map();

  /** @type {Map<string, HTMLElement>} */
  #readyPanelCache = new Map();

  /** @type {Map<string, 'recharge' | 'fallback'>} */
  #subscriptionUiMode = new Map();

  /** @type {Map<string, Promise<Array<{ id: number, name: string }>>>} */
  #sellingPlanCache = new Map();

  /** @type {string | null} */
  #purchasePanelSectionInstanceId = null;

  /** @type {Promise<string | null> | null} */
  #purchasePanelSectionInstanceIdPromise = null;

  /** @type {HTMLElement | null} */
  #selectedCard = null;

  /**
   * @typedef {{ id: number, available: boolean, featuredMediaId?: number | null }} StarterPackColourVariant
   * @typedef {{ optionName: string, defaultValue: string, products: Record<string, Record<string, StarterPackColourVariant>> }} StarterPackColourMaps
   */

  /** @type {StarterPackColourMaps | null} */
  #colourMaps = null;

  /** @type {string | null} */
  #selectedColourValue = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#colourMaps = this.#parseColourMaps();
    if (this.#colourMaps) {
      const checked = this.querySelector("[data-starter-pack-bottle-colour-input]:checked");
      this.#selectedColourValue =
        checked instanceof HTMLInputElement
          ? checked.value
          : this.#colourMaps.defaultValue || Object.keys(this.#colourMaps.products)[0] || null;
    }

    this.addEventListener("change", this.#onChange, { signal });
    this.addEventListener("click", this.#onCardClick, { signal });
    document.addEventListener(CartAddEvent.eventName, this.#onCartAddDone, { signal });
    document.addEventListener(CartErrorEvent.eventName, this.#onCartAddDone, { signal });

    const section = this.closest(".shopify-section");
    section?.addEventListener(ThemeEvents.variantUpdate, this.#syncAddToCartButtonState, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#syncAddToCartButtonState, { signal });

    this.#selectedCard = this.querySelector("[data-starter-pack-card].is-selected");
    this.#syncAddToCartButtonState();
    requestAnimationFrame(() => this.#syncAddToCartButtonState());
    window.setTimeout(() => this.#syncAddToCartButtonState(), 250);
    this.#hydrateCardPrices();
    this.#prefetchSubscriptionPanels();
    this.#loadPanelForSelectedCard();
    if (this.#colourMaps) {
      this.#applyColourToAllCards();
      this.#updateBottleColourSwatchStates();
    }
    const initialCard = this.#selectedCard;
    if (
      initialCard instanceof HTMLElement &&
      initialCard.dataset.oneTimeOnly === "true" &&
      !this.#cardHasColourVariants(initialCard)
    ) {
      void this.#syncOneTimeCardAvailability(initialCard).then(() => {
        this.#syncAddToCartButtonState();
      });
    }
    requestAnimationFrame(() => this.#syncGalleryMedia());
  }

  disconnectedCallback() {
    this.#panelFetchController?.abort();
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  #onCartAddDone = () => {
    this.#removeLoading();
  };

  /**
   * @param {Event} event
   */
  #onCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("[data-starter-pack-bottle-colour]")) return;
    if (target.closest(".recharge-subscription-widget, [data-starter-pack-selling-plan-fallback]")) return;

    const card = target.closest("[data-starter-pack-card]");
    if (!(card instanceof HTMLElement) || card.classList.contains("is-selected")) return;

    const radio = card.querySelector("[data-starter-pack-radio]");
    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
    }

    this.#selectCard(card);
  };

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches("[data-starter-pack-bottle-colour-input]")) {
      this.#onBottleColourChange(target);
      return;
    }
    this.#onOptionChange(event);
  };

  /**
   * @param {HTMLInputElement} input
   */
  #onBottleColourChange(input) {
    this.#selectedColourValue = input.value;
    this.#updateBottleColourLabel(input.value);
    this.#applyColourToAllCards();
    this.#updateBottleColourSwatchStates();
    this.#syncAddToCartButtonState();
    this.#syncGalleryMedia();
  }

  /**
   * @returns {StarterPackColourMaps | null}
   */
  #parseColourMaps() {
    const script = this.querySelector("[data-starter-pack-colour-maps]");
    if (!(script instanceof HTMLScriptElement) || !script.textContent?.trim()) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      if (!parsed?.products || typeof parsed.products !== "object") return null;
      return parsed;
    } catch (error) {
      console.warn("[starter-pack-selector] Could not parse bottle colour maps:", error);
      return null;
    }
  }

  /**
   * @param {string} value
   */
  #updateBottleColourLabel(value) {
    const label = this.querySelector("[data-starter-pack-bottle-colour-label]");
    if (label) label.textContent = value;
  }

  #applyColourToAllCards() {
    if (!this.#colourMaps || !this.#selectedColourValue) return;

    this.querySelectorAll("[data-starter-pack-card]").forEach((card) => {
      if (card instanceof HTMLElement) {
        this.#applyColourToCard(card);
      }
    });
  }

  /**
   * @param {HTMLElement} card
   */
  #cardHasColourVariants(card) {
    if (!this.#colourMaps) return false;

    const handle = card.dataset.productHandle;
    if (!handle) return false;

    const productMap = this.#colourMaps.products[handle];
    return !!productMap && Object.keys(productMap).length > 0;
  }

  /**
   * @param {HTMLElement} card
   */
  #applyColourToCard(card) {
    if (!this.#colourMaps || !this.#selectedColourValue) return;

    const handle = card.dataset.productHandle;
    if (!handle) return;

    const productMap = this.#colourMaps.products[handle];
    if (!productMap || Object.keys(productMap).length === 0) return;

    const variantInfo = productMap[this.#selectedColourValue];
    if (!variantInfo) {
      card.dataset.available = "false";
      card.dataset.unavailableReason = "unavailable";
      return;
    }

    card.dataset.available = variantInfo.available ? "true" : "false";
    card.dataset.unavailableReason = variantInfo.available ? "" : "sold-out";

    const variantInput = this.#getCardVariantInput(card);
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(variantInfo.id);
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {string | null}
   */
  #getMediaIdForCard(card) {
    const handle = card.dataset.productHandle;
    if (this.#colourMaps && this.#selectedColourValue && handle) {
      const productMap = this.#colourMaps.products[handle];
      const variantInfo = productMap?.[this.#selectedColourValue];
      if (variantInfo?.featuredMediaId) {
        return String(variantInfo.featuredMediaId);
      }
    }

    const featuredMediaId = card.dataset.featuredMediaId;
    return featuredMediaId ? String(featuredMediaId) : null;
  }

  #syncGalleryMedia() {
    const card = this.#selectedCard;
    if (!(card instanceof HTMLElement)) return;

    const mediaId = this.#getMediaIdForCard(card);
    if (!mediaId) return;

    const gallery = this.closest(".shopify-section")?.querySelector("media-gallery");
    if (!gallery) return;

    gallery.dispatchEvent(
      new CustomEvent("media:switch-by-id", {
        detail: { mediaId },
        bubbles: false,
      })
    );
  }

  /**
   * Legacy one-time cards without a colour option in the shared picker.
   *
   * @param {HTMLElement} card
   */
  async #syncOneTimeCardAvailability(card) {
    if (card.dataset.oneTimeOnly !== "true") return;
    if (this.#cardHasColourVariants(card)) return;

    const handle = card.dataset.productHandle;
    if (!handle) return;

    try {
      const url = new URL(`/products/${encodeURIComponent(handle)}.js`, window.location.origin);
      this.#appendPreviewThemeId(url);

      const response = await fetch(url.toString());
      if (!response.ok) return;

      const product = await response.json();
      const variantInput = card.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
      const variant =
        product.variants?.find((/** @type {{ id: number }} */ v) => String(v.id) === variantId) ??
        product.variants?.find((/** @type {{ available?: boolean }} */ v) => v.available) ??
        product.variants?.[0];

      card.dataset.available = variant?.available ? "true" : "false";
      card.dataset.unavailableReason = variant?.available ? "" : "sold-out";
    } catch (error) {
      console.warn(`[starter-pack-selector] One-time availability sync failed for ${handle}:`, error);
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLInputElement | null}
   */
  #getCardVariantInput(card) {
    if (card.dataset.oneTimeOnly === "true") {
      return card.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
    }

    const panel = card.querySelector("[data-starter-pack-purchase-panel]");
    return panel?.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
  }

  #updateBottleColourSwatchStates() {
    if (!this.#colourMaps) return;

    const card = this.#selectedCard ?? this.querySelector("[data-starter-pack-card].is-selected");
    const handle = card instanceof HTMLElement ? card.dataset.productHandle : null;
    const productMap = handle ? this.#colourMaps.products[handle] : null;
    const hasColourVariants = productMap && Object.keys(productMap).length > 0;

    this.querySelectorAll("[data-starter-pack-bottle-colour-input]").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = input.closest("label");
      const variantInfo =
        hasColourVariants && handle ? productMap?.[input.value] : null;
      const available = hasColourVariants ? (variantInfo?.available ?? false) : true;

      input.removeAttribute("disabled");
      input.setAttribute("aria-disabled", available ? "false" : "true");
      label?.classList.toggle("variant-option__button-label--unavailable", !available);
    });
  }

  /**
   * @param {Event} event
   */
  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches("[data-starter-pack-radio]")) return;

    const card = target.closest("[data-starter-pack-card]");
    if (!(card instanceof HTMLElement)) return;

    this.#selectCard(card);
  };

  /**
   * @param {HTMLElement} card
   */
  #selectCard(card) {
    if (this.#selectedCard === card && card.classList.contains("is-selected")) {
      return;
    }

    this.querySelectorAll("[data-starter-pack-card]").forEach((el) => {
      el.classList.remove("is-selected");
    });
    card.classList.add("is-selected");
    this.#selectedCard = card;

    this.querySelectorAll("[data-starter-pack-panel-slot]").forEach((slot) => {
      slot.hidden = true;
      slot.innerHTML = "";
    });

    this.#syncAddToCartButtonState();

    if (card.dataset.oneTimeOnly === "true") {
      this.#panelFetchController?.abort();
      this.#showPanelLoading(false);
      this.#applyColourToCard(card);
      if (this.#cardHasColourVariants(card)) {
        this.#syncAddToCartButtonState();
      } else {
        void this.#syncOneTimeCardAvailability(card).then(() => {
          this.#syncAddToCartButtonState();
        });
      }
      this.#updateBottleColourSwatchStates();
      this.#syncGalleryMedia();
      return;
    }

    const slot = card.querySelector("[data-starter-pack-panel-slot]");
    if (slot instanceof HTMLElement) {
      slot.hidden = false;
    }

    this.#loadPanelForSelectedCard();
    this.#updateBottleColourSwatchStates();
    this.#syncGalleryMedia();
  }

  async #loadPanelForSelectedCard() {
    const card = this.#selectedCard ?? this.querySelector("[data-starter-pack-card].is-selected");
    if (!(card instanceof HTMLElement)) return;

    if (card.dataset.oneTimeOnly === "true") {
      this.#showPanelLoading(false);
      return;
    }

    const handle = card.dataset.productHandle;
    const slot = card.querySelector("[data-starter-pack-panel-slot]");
    if (!handle || !(slot instanceof HTMLElement)) return;

    const ready = this.#readyPanelCache.get(handle);
    if (ready) {
      slot.hidden = false;
      slot.innerHTML = "";
      const panel = /** @type {HTMLElement} */ (ready.cloneNode(true));
      slot.appendChild(panel);
      void this.#ensurePanelSubscriptionUi(panel, handle).then(() => {
        if (this.#selectedCard === card) {
          this.#rememberReadyPanel(handle, panel);
        }
      });
      this.#applyColourToCard(card);
      this.#updateBottleColourSwatchStates();
      this.#showPanelLoading(false);
      this.#syncAddToCartButtonState();
      return;
    }

    const cached = this.#panelCache.get(handle);
    if (cached) {
      slot.hidden = false;
      slot.innerHTML = "";
      const panel = /** @type {HTMLElement} */ (cached.cloneNode(true));
      slot.appendChild(panel);
      void this.#ensurePanelSubscriptionUi(panel, handle).then(() => {
        if (this.#selectedCard === card) {
          this.#rememberReadyPanel(handle, panel);
        }
      });
      this.#applyColourToCard(card);
      this.#updateBottleColourSwatchStates();
      this.#showPanelLoading(false);
      this.#syncAddToCartButtonState();
      return;
    }

    this.#panelFetchController?.abort();
    this.#panelFetchController = new AbortController();
    const { signal } = this.#panelFetchController;
    const loadToken = ++this.#panelLoadToken;

    this.#showPanelLoading(true);

    try {
      const panel = await this.#fetchPurchasePanel(handle, signal);
      if (loadToken !== this.#panelLoadToken) return;

      if (!panel) {
        slot.innerHTML = "";
        return;
      }

      this.#panelCache.set(handle, panel);
      if (this.#selectedCard === card) {
        slot.hidden = false;
        slot.innerHTML = "";
        const mountedPanel = /** @type {HTMLElement} */ (panel.cloneNode(true));
        slot.appendChild(mountedPanel);
        void this.#ensurePanelSubscriptionUi(mountedPanel, handle).then(() => {
          if (this.#selectedCard === card) {
            this.#rememberReadyPanel(handle, mountedPanel);
          }
        });
        this.#applyColourToCard(card);
        this.#updateBottleColourSwatchStates();
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      console.warn("[starter-pack-selector] Purchase panel fetch failed:", error);
    } finally {
      if (loadToken === this.#panelLoadToken) {
        this.#showPanelLoading(false);
        this.#syncAddToCartButtonState();
      }
    }
  }

  /** Sync shared ATC disabled state and label from the selected card. */
  #syncAddToCartButtonState = () => {
    const card = this.#selectedCard ?? this.querySelector("[data-starter-pack-card].is-selected");
    const button = this.refs.addToCartButton;
    const textEl = this.refs.addToCartText;
    if (!(card instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

    const availableLabel = button.dataset.addToCartText || textEl?.textContent || "";
    const soldOutLabel = button.dataset.soldOutText || "Sold out";
    const unavailableLabel = button.dataset.unavailableText || "Unavailable";

    if (card.dataset.available === "true") {
      button.disabled = false;
      button.removeAttribute("disabled");
      button.setAttribute("aria-disabled", "false");
      if (textEl) textEl.textContent = availableLabel;
      button.setAttribute("aria-label", availableLabel);
      return;
    }

    button.disabled = true;
    button.setAttribute("aria-disabled", "true");

    const reason = card.dataset.unavailableReason;
    const disabledLabel = reason === "unavailable" ? unavailableLabel : soldOutLabel;
    if (textEl) textEl.textContent = disabledLabel;
    button.setAttribute("aria-label", disabledLabel);
  };

  /** @type {Map<string, Promise<void>>} */
  #priceHydratePromises = new Map();

  #hydrateCardPrices() {
    this.querySelectorAll('[data-starter-pack-card][data-one-time-only="false"]').forEach((card) => {
      if (card instanceof HTMLElement) {
        void this.#hydrateCardPrice(card);
      }
    });
  }

  /**
   * Subscription cards: one-time (RRP) struck through + first selling-plan price from product JSON.
   *
   * @param {HTMLElement} card
   */
  async #hydrateCardPrice(card) {
    const handle = card.dataset.productHandle;
    if (!handle) return;

    const existing = this.#priceHydratePromises.get(handle);
    if (existing) return existing;

    const promise = (async () => {
      const priceEl = card.querySelector(".starter-pack-selector__price");
      if (!(priceEl instanceof HTMLElement)) return;

      try {
        const url = new URL(`/products/${encodeURIComponent(handle)}.js`, window.location.origin);
        this.#appendPreviewThemeId(url);

        const response = await fetch(url.toString());
        if (!response.ok) return;

        const product = await response.json();
        const variant = product.variants?.find((/** @type {{ available?: boolean }} */ v) => v.available) ?? product.variants?.[0];
        const allocation = this.#pickDefaultSellingPlanAllocation(variant?.selling_plan_allocations);
        if (!allocation) return;

        const rrp = allocation.compare_at_price ?? variant.price;
        const subscriptionPrice = allocation.price;

        priceEl.innerHTML = `<s class="starter-pack-selector__compare-at text-body-2">${formatCurrency(rrp)}</s><span class="starter-pack-selector__current-price text-body-2">${formatCurrency(subscriptionPrice)}</span>`;
      } catch (error) {
        console.warn(`[starter-pack-selector] Price hydrate failed for ${handle}:`, error);
      }
    })();

    this.#priceHydratePromises.set(handle, promise);
    return promise;
  }

  /** @type {AbortController | null} */
  #panelFetchController = null;

  /** @type {number} */
  #panelLoadToken = 0;

  /**
   * @returns {HTMLElement | null}
   */
  #getPanelLoadingEl() {
    const ref = this.refs.panelLoading;
    if (ref instanceof HTMLElement) return ref;
    const el = this.querySelector(".starter-pack-selector__panel-loading");
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Section Rendering API must use the template JSON section instance id
   * (e.g. template--…__starter_pack_purchase_panel), not the section filename,
   * or @app blocks such as Recharge are omitted from the response.
   *
   * @param {string} productHandle
   * @param {AbortSignal} signal
   * @returns {Promise<string | null>}
   */
  async #resolvePurchasePanelSectionInstanceId(productHandle, signal) {
    if (this.#purchasePanelSectionInstanceId) {
      return this.#purchasePanelSectionInstanceId;
    }

    if (this.#purchasePanelSectionInstanceIdPromise) {
      return this.#purchasePanelSectionInstanceIdPromise;
    }

    const configured = this.dataset.purchasePanelSectionId?.trim() || "starter_pack_purchase_panel";

    if (configured.includes("__")) {
      this.#purchasePanelSectionInstanceId = configured;
      return configured;
    }

    this.#purchasePanelSectionInstanceIdPromise = (async () => {
      const url = new URL(`/products/${encodeURIComponent(productHandle)}`, window.location.origin);
      this.#appendPreviewThemeId(url);

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return null;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const panel = doc.querySelector("[data-starter-pack-purchase-panel]");
      const sectionId = panel instanceof HTMLElement ? panel.dataset.sectionId?.trim() : null;

      if (sectionId) {
        this.#purchasePanelSectionInstanceId = sectionId;
      }

      return sectionId ?? null;
    })();

    try {
      return await this.#purchasePanelSectionInstanceIdPromise;
    } finally {
      this.#purchasePanelSectionInstanceIdPromise = null;
    }
  }

  /**
   * @param {URL} url
   */
  #appendPreviewThemeId(url) {
    const previewThemeId = new URLSearchParams(window.location.search).get("preview_theme_id");
    if (previewThemeId) {
      url.searchParams.set("preview_theme_id", previewThemeId);
    }
  }

  async #fetchPurchasePanel(productHandle, signal) {
    const sectionId = await this.#resolvePurchasePanelSectionInstanceId(productHandle, signal);
    if (!sectionId) {
      console.warn(
        `[starter-pack-selector] Could not resolve purchase panel section id for ${productHandle}`,
      );
      return null;
    }

    const url = new URL(`/products/${encodeURIComponent(productHandle)}`, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    this.#appendPreviewThemeId(url);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) {
      console.warn(`[starter-pack-selector] Failed to fetch purchase panel for ${productHandle}: ${response.status}`);
      return null;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const panel =
      doc.querySelector("[data-starter-pack-purchase-panel]") ??
      doc.querySelector(".starter-pack-purchase-panel") ??
      doc.querySelector(`#shopify-section-${sectionId} [data-starter-pack-purchase-panel]`) ??
      doc.querySelector(`#shopify-section-${sectionId} .starter-pack-purchase-panel`);

    return panel instanceof HTMLElement ? panel : null;
  }

  #prefetchSubscriptionPanels() {
    this.querySelectorAll('[data-starter-pack-card][data-one-time-only="false"]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const handle = card.dataset.productHandle;
      if (!handle) return;

      void this.#getSellingPlans(handle);

      if (this.#panelCache.has(handle)) return;

      void this.#fetchPurchasePanel(handle)
        .then((panel) => {
          if (panel instanceof HTMLElement) {
            this.#panelCache.set(handle, panel);
          }
        })
        .catch(() => {});
    });
  }

  /**
   * @param {string} productHandle
   */
  #rememberReadyPanel(productHandle, panel) {
    this.#readyPanelCache.set(productHandle, /** @type {HTMLElement} */ (panel.cloneNode(true)));
  }

  /**
   * Recharge SSR includes SubscriptionWidgetConfig when the widget can mount.
   * Section API stubs only set window.Recharge = {} — waiting on them always times out.
   *
   * @param {HTMLElement} container
   */
  #isRechargeWidgetStub(container) {
    const widgetBlock = container.querySelector(".recharge-subscription-widget");
    if (!(widgetBlock instanceof HTMLElement)) return false;
    if (container.querySelector("recharge-subscription-widget")) return false;
    return !widgetBlock.innerHTML.includes("SubscriptionWidgetConfig");
  }

  /**
   * @param {HTMLElement} container
   * @param {string} productHandle
   */
  #wirePanelSubscriptionUi(container, productHandle) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    const fallbackSelect = panel.querySelector("[data-starter-pack-selling-plan-fallback] select");
    if (fallbackSelect instanceof HTMLSelectElement) {
      delete fallbackSelect.dataset.starterPackSellingPlanBound;
    }

    delete panel.dataset.starterPackSellingPlanBound;
    this.#bindSellingPlanChangeListeners(container);
    this.#syncSellingPlanFromUi(container);
    requestAnimationFrame(() => {
      const select = panel.querySelector("[data-starter-pack-selling-plan-fallback] select");
      if (select instanceof HTMLSelectElement) {
        window.RdFrequencySelectFontFit?.register?.(select);
      }
    });
  }

  /**
   * @param {string} productHandle
   * @returns {Promise<Array<{ id: number, name: string }>>}
   */
  async #getSellingPlans(productHandle) {
    const cached = this.#sellingPlanCache.get(productHandle);
    if (cached) return cached;

    const promise = (async () => {
      const url = new URL(`/products/${encodeURIComponent(productHandle)}.js`, window.location.origin);
      this.#appendPreviewThemeId(url);

      const response = await fetch(url.toString());
      if (!response.ok) return [];

      const product = await response.json();
      /** @type {Array<{ id: number, name: string }>} */
      const plans = [];

      for (const group of product.selling_plan_groups ?? []) {
        for (const plan of group.selling_plans ?? []) {
          if (plan?.id) {
            plans.push({ id: plan.id, name: plan.name || `Plan ${plan.id}` });
          }
        }
      }

      return plans;
    })();

    this.#sellingPlanCache.set(productHandle, promise);
    return promise;
  }

  /**
   * Starter-pack cards use a theme-controlled frequency dropdown so the hidden
   * selling_plan input stays in sync. The Recharge app block is stripped from
   * injected panels — on panel 1 it often hydrates first and overrides the form
   * with its own plan even when the visible select disagrees.
   *
   * @param {HTMLElement} container
   * @param {string} productHandle
   */
  async #ensurePanelSubscriptionUi(container, productHandle) {
    this.#removeRechargeFromPanel(container);
    await this.#mountSellingPlanFallback(container, productHandle);
    this.#wirePanelSubscriptionUi(container, productHandle);
    this.#subscriptionUiMode.set(productHandle, "fallback");
  }

  /**
   * @param {HTMLElement} container
   */
  #removeRechargeFromPanel(container) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    panel.querySelectorAll(".recharge-subscription-widget").forEach((block) => {
      block.remove();
    });
    panel.querySelectorAll("recharge-subscription-widget").forEach((widget) => {
      widget.remove();
    });

    delete panel.dataset.starterPackFrequencyDefaulted;
    delete panel.dataset.starterPackSellingPlanBound;
  }

  /**
   * @param {HTMLElement} container
   * @param {number} timeoutMs
   */
  #waitForRechargeWidget(container, timeoutMs) {
    return new Promise((resolve) => {
      const started = Date.now();

      const check = () => {
        const widget = container.querySelector("recharge-subscription-widget");
        if (widget instanceof HTMLElement && widget.shadowRoot && widget.shadowRoot.childElementCount > 0) {
          resolve(true);
          return;
        }

        if (Date.now() - started >= timeoutMs) {
          resolve(false);
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }

  /**
   * @param {HTMLElement} container
   * @param {string} productHandle
   */
  async #mountSellingPlanFallback(container, productHandle) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    const widgets = panel.querySelector(".starter-pack-purchase-panel__widgets");
    if (!(widgets instanceof HTMLElement)) return;

    const existingFallback = widgets.querySelector("[data-starter-pack-selling-plan-fallback]");
    if (existingFallback) {
      this.#wirePanelSubscriptionUi(container, productHandle);
      return;
    }

    try {
      const plans = await this.#getSellingPlans(productHandle);
      if (plans.length === 0) return;

      const card = this.querySelector(`[data-starter-pack-card][data-product-handle="${CSS.escape(productHandle)}"]`);
      const days = this.#getDefaultFrequencyDays(card);
      const pattern = new RegExp(`\\b${days}\\b`);
      const preferred =
        plans.find((plan) => pattern.test(plan.name)) ?? plans.at(-1) ?? plans[0];

      const fallback = document.createElement("div");
      fallback.className = "starter-pack-selling-plan-fallback";
      fallback.dataset.starterPackSellingPlanFallback = "";

      const label = document.createElement("label");
      label.className = "starter-pack-selling-plan-fallback__label visually-hidden";
      label.textContent = "Delivery frequency";
      label.htmlFor = `starter-pack-frequency-${productHandle}`;

      const select = document.createElement("select");
      select.id = `starter-pack-frequency-${productHandle}`;
      select.className = "starter-pack-selling-plan-fallback__select";
      select.setAttribute("aria-label", "Delivery frequency");

      for (const plan of plans) {
        const option = document.createElement("option");
        option.value = String(plan.id);
        option.textContent = plan.name;
        select.appendChild(option);
      }

      select.value = String(preferred.id);
      fallback.append(label, select);
      widgets.appendChild(fallback);
      this.#syncSellingPlanInput(panel, select.value);
    } catch (error) {
      console.warn(`[starter-pack-selector] Selling plan fallback failed for ${productHandle}:`, error);
    }
  }

  /**
   * @param {HTMLElement} container
   */
  #bindSellingPlanChangeListeners(container) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    const fallbackSelect = panel.querySelector("[data-starter-pack-selling-plan-fallback] select");
    if (fallbackSelect instanceof HTMLSelectElement) {
      if (fallbackSelect.dataset.starterPackSellingPlanBound === "true") return;

      fallbackSelect.dataset.starterPackSellingPlanBound = "true";
      fallbackSelect.addEventListener("change", () => {
        this.#syncSellingPlanInput(panel, fallbackSelect.value);
      });
      window.RdFrequencySelectFontFit?.register?.(fallbackSelect);
    }
  }

  /**
   * Read the active frequency control (fallback or Recharge) into the hidden form input.
   *
   * @param {HTMLElement} container
   */
  #syncSellingPlanFromUi(container) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    const fallbackSelect = panel.querySelector("[data-starter-pack-selling-plan-fallback] select");
    if (fallbackSelect instanceof HTMLSelectElement) {
      this.#syncSellingPlanInput(panel, fallbackSelect.value);
    }
  }

  /**
   * Recharge Subscription Widget v2 does not auto-init when HTML is injected after load.
   * @see https://docs.getrecharge.com/docs/using-quick-view-with-the-subscription-widget
   *
   * @param {HTMLElement} container
   */
  #hydrateRechargeWidget(container) {
    const widget = container.querySelector(".recharge-subscription-widget");
    if (!(widget instanceof HTMLElement)) return;

    widget.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    const webComponent = widget.querySelector("recharge-subscription-widget");
    if (webComponent?.parentNode) {
      webComponent.parentNode.replaceChild(webComponent.cloneNode(true), webComponent);
    }

    this.#selectDefaultSubscriptionFrequency(container);
  }

  /**
   * @returns {number}
   */
    #getDefaultFrequencyDays(card) {
    const raw = card?.dataset.defaultFrequencyDays ?? this.dataset.defaultFrequencyDays;
    const days = Number.parseInt(raw ?? "90", 10);
    return Number.isFinite(days) && days > 0 ? days : 90;
  }

  /**
   * Prefer the longest-interval allocation when product JSON has no plan labels.
   *
   * @param {Array<{ price?: number, compare_at_price?: number }> | undefined} allocations
   */
  #pickDefaultSellingPlanAllocation(allocations) {
    if (!allocations?.length) return null;
    return allocations.at(-1) ?? allocations[0];
  }

  /**
   * @param {ParentNode} root
   * @returns {HTMLSelectElement | null}
   */
  #findRechargeFrequencySelect(root) {
    const select = root.querySelector('select[part="rc-plans-select"], select');
    if (select instanceof HTMLSelectElement) return select;

    for (const el of root.querySelectorAll("*")) {
      if (el instanceof HTMLElement && el.shadowRoot) {
        const found = this.#findRechargeFrequencySelect(el.shadowRoot);
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Recharge defaults to the first plan (usually 30 days). Select the configured interval instead.
   *
   * @param {HTMLElement} container
   */
  #selectDefaultSubscriptionFrequency(container) {
    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");
    if (!(panel instanceof HTMLElement)) return;

    if (panel.dataset.starterPackFrequencyDefaulted === "true") {
      this.#syncSellingPlanFromUi(container);
      return;
    }

    const days = this.#getDefaultFrequencyDays();
    const pattern = new RegExp(`\\b${days}\\b`);

    const attemptSelect = () => {
      const widget = container.querySelector("recharge-subscription-widget");
      if (!(widget instanceof HTMLElement) || !widget.shadowRoot) return false;

      const select = this.#findRechargeFrequencySelect(widget.shadowRoot);
      if (!(select instanceof HTMLSelectElement) || select.options.length === 0) return false;

      const preferred =
        Array.from(select.options).find((option) => pattern.test(option.text)) ??
        Array.from(select.options).at(-1);

      if (!preferred) return false;

      if (select.value !== preferred.value) {
        select.value = preferred.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        select.dispatchEvent(new Event("input", { bubbles: true }));
      }

      panel.dataset.starterPackFrequencyDefaulted = "true";
      this.#bindSellingPlanChangeListeners(container);
      this.#syncSellingPlanInput(container, select.value);
      return true;
    };

    const trySelect = (attempt = 0) => {
      if (attemptSelect()) return;
      if (attempt < 40) requestAnimationFrame(() => trySelect(attempt + 1));
    };

    trySelect();
  }

  /**
   * Keep the hidden add-to-cart selling plan in sync when Recharge init lags behind the select.
   *
   * @param {HTMLElement} container
   * @param {string} planId
   */
  #syncSellingPlanInput(container, planId) {
    if (!planId) return;

    const panel = container.matches("[data-starter-pack-purchase-panel]")
      ? container
      : container.querySelector("[data-starter-pack-purchase-panel]");

    const input = panel?.querySelector(
      '[name="selling_plan"], [data-starter-pack-selling-plan-input]',
    );
    if (!(input instanceof HTMLInputElement) || input.value === planId) return;

    input.value = planId;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * @param {boolean} loading
   */
  #showPanelLoading(loading) {
    const el = this.#getPanelLoadingEl();
    if (!el) return;
    el.hidden = !loading;
    el.classList.toggle("is-loading", loading);
    el.setAttribute("aria-hidden", loading ? "false" : "true");
  }

  #addLoading() {
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner.classList.remove("hidden");
    this.refs.addToCartButton.disabled = true;
  }

  #removeLoading() {
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner.classList.add("hidden");
    this.#syncAddToCartButtonState();
  }

  /**
   * @param {Event} event
   */
  handleAddToCartClick(event) {
    event.preventDefault();

    const card = this.#selectedCard ?? this.querySelector("[data-starter-pack-card].is-selected");
    if (!(card instanceof HTMLElement)) return;

    this.#addLoading();
    this.refs.addToCartTextError?.classList.add("hidden");

    if (card.dataset.oneTimeOnly === "true") {
      const formComponent = card.querySelector("product-form-component");
      const form = formComponent?.querySelector("form[data-type='add-to-cart-form']");
      if (form instanceof HTMLFormElement) {
        this.#applyColourToCard(card);
        form.requestSubmit();
        return;
      }
    }

    const panel = card.querySelector("[data-starter-pack-purchase-panel]");
    const formComponent = panel?.querySelector("product-form-component");
    const form = formComponent?.querySelector("form[data-type='add-to-cart-form']");

    if (form instanceof HTMLFormElement) {
      this.#applyColourToCard(card);
      if (panel instanceof HTMLElement) {
        this.#syncSellingPlanFromUi(panel);
      }
      form.requestSubmit();
      return;
    }

    this.#removeLoading();
    if (this.refs.addToCartTextError) {
      this.refs.addToCartTextError.textContent = "Subscription options are still loading. Please try again.";
      this.refs.addToCartTextError.classList.remove("hidden");
    }
  }
}

if (!customElements.get("starter-pack-selector")) {
  customElements.define("starter-pack-selector", StarterPackSelector);
}

export { StarterPackSelector };
